import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { Target, Lock, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate("/admin/login");
    }
  }, [token, navigate]);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] relative"
      style={{
        backgroundImage:
          "url(https://static.prod-images.emergentagent.com/jobs/f707a822-bbe1-4c69-96b7-9259b8de4d00/images/fbdf8f044500ad2fe4d88a49bf647ff10fc9a081bcebea33ee50a27fb0f33e44.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <Target className="w-12 h-12 text-[#E3292E] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-display text-3xl text-white uppercase tracking-tighter">Reset Password</h1>
          </div>

          <Card className="bg-[#141414]/90 backdrop-blur-sm border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="font-heading text-white text-xl">
                {success ? "Password Reset!" : "Set New Password"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {success ? (
                <div className="text-center space-y-4 py-4">
                  <CheckCircle className="w-12 h-12 text-[#309F6A] mx-auto" />
                  <p className="text-white/60 text-sm font-body">
                    Your password has been reset. You can now log in with your new password.
                  </p>
                  <Button
                    onClick={() => navigate("/admin/login")}
                    className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading font-bold"
                    data-testid="go-to-login-btn"
                  >
                    <Lock className="mr-2 h-4 w-4" /> GO TO LOGIN
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">
                      New Password
                    </Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                      placeholder="Min 6 characters"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">
                      Confirm Password
                    </Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                      placeholder="Re-enter password"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none py-5 font-heading font-bold"
                    data-testid="reset-submit-btn"
                  >
                    {loading ? "Resetting..." : "RESET PASSWORD"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <Button
            onClick={() => navigate("/admin/login")}
            variant="ghost"
            className="w-full mt-4 text-white/40 hover:text-white hover:bg-white/5 rounded-none"
            data-testid="back-to-login-btn"
          >
            Back to Login
          </Button>
        </div>
      </div>
    </div>
  );
}
